/**
 * PATH: packages/email-hub/src/feature/compose/brandingDefaultSync.ts
 * FILE_ID: EMAIL-COMPOSE-LIB-BRANDINGSYNC-001
 * PURPOSE: Background-refresh the "Powered by Quotech" footer default
 *          cache (@quotech/ui brandingPref) from the backend email
 *          preferences so the composer's footer chip reflects the user's
 *          saved choice on EVERY device.
 * WHY: FEAT-2026-07-07-GTM-FOOTER-OPT-IN — the footer chip renders at
 *      composer mount and cannot await a fetch, so it reads the
 *      localStorage cache (default OFF, the backend's opt-in default).
 *      EmailTab writes the cache same-device on save; this fetch closes
 *      the fresh-device / other-device gap. Same pattern as undoSendSync.
 * FLOW:
 *   composer mount → syncBrandingDefaultFromServer() ──(throttled, TTL)──▶
 *     GET /api/settings/email/ → setQuotechBrandingDefault(quotech_branding)
 * RELATED:
 *   - pages/useComposePage.ts: seeds the chip state + sends the override.
 *   - packages/ui/src/lib/brandingPref.ts: the cache.
 *   - undoSendSync.ts: sibling cache-then-refresh module.
 */
import { setQuotechBrandingDefault } from '@quotech/ui'

import { emailApiFetch } from '../lib/api'

export { getQuotechBrandingDefault, setQuotechBrandingDefault } from '@quotech/ui'

/** Re-fetch at most this often — the value changes rarely. */
const SYNC_TTL_MS = 5 * 60 * 1000

let lastSyncAt = Number.NEGATIVE_INFINITY
let inFlight: Promise<void> | null = null

/**
 * Refresh the cached default from the server, at most once per TTL. Safe to
 * call on every composer mount: coalesces concurrent calls, throttles by
 * time, and never throws. `now`/`fetchImpl` are injectable for tests.
 */
export function syncBrandingDefaultFromServer(
  now: number = Date.now(),
  fetchImpl: typeof emailApiFetch = emailApiFetch,
): Promise<void> {
  if (inFlight) return inFlight
  if (now - lastSyncAt < SYNC_TTL_MS) return Promise.resolve()
  lastSyncAt = now // throttle regardless of outcome so failures don't hammer
  inFlight = (async () => {
    try {
      const res = await fetchImpl('/api/settings/email/')
      if (!res.ok) return
      const data = await res.json().catch(() => null)
      const prefs = data?.preferences ?? data
      if (prefs && typeof prefs === 'object' && 'quotech_branding' in prefs) {
        setQuotechBrandingDefault(prefs.quotech_branding === true)
      }
    } catch {
      /* offline / endpoint unavailable — keep the cached value */
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

/** Test-only: reset the module throttle state. */
export function __resetBrandingSyncForTests(): void {
  lastSyncAt = Number.NEGATIVE_INFINITY
  inFlight = null
}
